import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { AirIdDisplay } from '@/components/air-id-display';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { getStudentGroupAttendancePolicyV2History } from '@/lib/api-calls';
import { formatAttendancePolicyV2UsageFraction } from '@/lib/attendance-policy-v2-display';
import { AttendancePolicyV2StatusBadge } from './status-badge';
import { AttendancePolicyV2HistoryModalItem } from './history-modal-item';

type AttendancePolicyV2HistoryModalProps = {
  studentGroupId: string;
  teacherName: string | null;
};

export const AttendancePolicyV2HistoryModal = ({
  studentGroupId,
  teacherName,
}: AttendancePolicyV2HistoryModalProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const {
    data: history,
    isPending,
    isError,
    refetch,
  } = useQuery({
    ...getStudentGroupAttendancePolicyV2History(studentGroupId),
    enabled: isOpen,
  });

  const { overview, completedEvents, activeFutureSubstituteRequests, protection } = history ?? {};

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid={`teacher-attendance-modal-button-view-history-${studentGroupId}`}>
          View history
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto" data-testid="teacher-attendance-modal-history">
        <DialogHeader>
          <DialogTitle className="font-bold">Attendance history</DialogTitle>
          <div className="pt-1 text-sm text-muted-foreground">
            {teacherName ?? 'Unassigned teacher'} -{' '}
            <div className="items-center inline-flex gap-1">
              Class <AirIdDisplay entityType="cls" rawId={studentGroupId} showCopyButton={true} />
            </div>
          </div>
        </DialogHeader>

        {isPending ? (
          <div className="py-8 text-sm text-muted-foreground">Loading attendance history</div>
        ) : isError ? (
          <div className="flex flex-col items-start gap-3 py-8">
            <div className="text-sm text-muted-foreground">Failed to load attendance history. Please try again.</div>
            <Button variant="outline" onClick={() => void refetch()}>
              Try again
            </Button>
          </div>
        ) : !history || !overview ? (
          <div className="py-8 text-sm text-muted-foreground">No attendance history available.</div>
        ) : (
          <div className="flex flex-col gap-6">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 p-4 rounded-md border bg-slate-100 border-slate-300">
              <div className="flex flex-col gap-1 pr-4 border-r border-slate-300">
                <div className="text-xs text-muted-foreground">V2 status</div>
                <AttendancePolicyV2StatusBadge actionLevel={overview.actionLevel} />
              </div>
              <div className="flex flex-col gap-1 sm:border-r sm:pr-4 sm:border-slate-300">
                <div className="text-xs text-muted-foreground">Eligible sessions</div>
                <div className="text-2xl font-semibold">{overview.eligibleSessionCount}</div>
              </div>
              <div className="flex flex-col gap-1 pr-4 border-r border-slate-300">
                <div className="text-xs text-muted-foreground">Total usage</div>
                <div className="text-2xl font-semibold">
                  {formatAttendancePolicyV2UsageFraction(
                    overview.totalSubRequestUsage,
                    overview.totalSubRequestAllowance,
                  )}
                </div>
              </div>
              <div className="flex flex-col gap-1">
                <div className="text-xs text-muted-foreground">Short notice</div>
                <div className="text-2xl font-semibold">
                  {formatAttendancePolicyV2UsageFraction(
                    overview.shortNoticeSubRequestUsage,
                    overview.shortNoticeSubRequestAllowance,
                  )}
                </div>
              </div>
            </div>
            <section className="flex flex-col gap-3">
              <h3 className="text-md font-semibold">Policy events</h3>
              {completedEvents?.length === 0 ? (
                <div className="text-sm text-muted-foreground">No policy events recorded.</div>
              ) : (
                <ul className="flex flex-col">
                  {completedEvents?.map(
                    ({ sessionId, kind, eventPlainDate, title, explanation, outcomeLabel }, index) => (
                      <AttendancePolicyV2HistoryModalItem
                        key={`${sessionId}-${kind}-${eventPlainDate.toString()}-${index}`}
                        kind={kind}
                        eventPlainDate={eventPlainDate}
                        title={title}
                        explanation={explanation}
                        outcomeLabel={outcomeLabel}
                      />
                    ),
                  )}
                </ul>
              )}
            </section>
            <section className="flex flex-col gap-3">
              <h3 className="text-md font-semibold">Active future substitute requests</h3>
              {activeFutureSubstituteRequests?.length === 0 ? (
                <div className="text-sm text-muted-foreground">No active future substitute requests.</div>
              ) : (
                <ul className="flex flex-col">
                  {activeFutureSubstituteRequests?.map(
                    ({ sessionId, kind, eventPlainDate, title, explanation, outcomeLabel }, index) => (
                      <AttendancePolicyV2HistoryModalItem
                        key={`${sessionId}-${kind}-${eventPlainDate.toString()}-${index}`}
                        kind={kind}
                        eventPlainDate={eventPlainDate}
                        title={title}
                        explanation={explanation}
                        outcomeLabel={outcomeLabel}
                      />
                    ),
                  )}
                </ul>
              )}
            </section>
            <section className="flex flex-col gap-3">
              <h3 className="text-md font-semibold">Protection</h3>
              <div className="p-4 text-sm text-muted-foreground rounded-md border border-slate-300 bg-slate-100">
                Final 14-day protection:{' '}
                {protection?.final14DayActive ? 'active - attendance reassignment is blocked' : 'not active'}.
              </div>
            </section>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
